import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

/**
 * PageLoader - Skeleton loading UI with auto-recovery mechanism.
 * Triggers after 8 seconds of inactivity.
 */
export const PageLoader: React.FC = () => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        if (next >= 8) setShowRetry(true); // Show retry after 8s
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-10 animate-in fade-in duration-500">
      {/* Loading indicator with timer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
             <Loader2 size={18} className="text-indigo-400 animate-spin" />
          </div>
          <span className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em]">
            Đang đồng bộ dữ liệu ({elapsedSeconds}s)
          </span>
        </div>
        {showRetry && (
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-500/20 transition-colors"
          >
            <RefreshCw size={14} />
            Re-Sync Node
          </button>
        )}
      </div>

      {/* Skeleton UI - Glass Variant */}
      <div className="animate-pulse space-y-8">
        {/* Header Skeleton */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-3">
            <div className="h-8 w-64 bg-white/5 border border-white/5 rounded-2xl"></div>
            <div className="h-3 w-48 bg-white/5 rounded-lg opacity-50"></div>
          </div>
          <div className="h-10 w-32 bg-white/5 border border-white/10 rounded-xl"></div>
        </div>

        {/* Cards Skeleton Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white/5 rounded-[2rem] border border-white/5 p-8 relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
              <div className="flex items-start justify-between gap-3 mb-6">
                <div className="h-6 bg-white/10 rounded-xl w-3/4"></div>
                <div className="h-6 bg-white/10 rounded-full w-16"></div>
              </div>
              <div className="h-3 bg-white/5 rounded-lg w-1/3 mb-8"></div>
              <div className="bg-black/20 rounded-2xl p-6 mb-6 border border-white/5">
                <div className="h-2 bg-white/10 rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-2 bg-white/5 rounded w-full"></div>
                  <div className="h-2 bg-white/5 rounded w-5/6"></div>
                </div>
              </div>
              <div className="h-14 bg-white/5 border border-white/5 rounded-2xl w-full mt-4"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageLoader;
